import { useState, useEffect } from "react";
import minzoLogo from "@/assets/minzo-logo.webp";

interface PreloaderProps {
  onComplete?: () => void;
  minDisplayTime?: number;
  maxDisplayTime?: number;
  isContentReady?: boolean;
}

export function Preloader({
  onComplete,
  minDisplayTime = 800,
  maxDisplayTime = 4000,
  isContentReady = false,
}: PreloaderProps) {
  const [isVisible, setIsVisible] = useState(true);
  const [isAnimatingOut, setIsAnimatingOut] = useState(false);
  const [minTimePassed, setMinTimePassed] = useState(false);

  // Track minimum display time
  useEffect(() => {
    const timer = setTimeout(() => setMinTimePassed(true), minDisplayTime);
    return () => clearTimeout(timer);
  }, [minDisplayTime]);

  // Dismiss when both min time passed AND content is ready
  useEffect(() => {
    if (minTimePassed && isContentReady && !isAnimatingOut) {
      setIsAnimatingOut(true);
      setTimeout(() => {
        setIsVisible(false);
        onComplete?.();
      }, 600);
    }
  }, [minTimePassed, isContentReady, isAnimatingOut, onComplete]);

  // Force dismiss after max time regardless
  useEffect(() => {
    const timer = setTimeout(() => {
      if (!isAnimatingOut) {
        setIsAnimatingOut(true);
        setTimeout(() => {
          setIsVisible(false);
          onComplete?.();
        }, 600);
      }
    }, maxDisplayTime);
    return () => clearTimeout(timer);
  }, [maxDisplayTime, isAnimatingOut, onComplete]);

  if (!isVisible) return null;

  return (
    <div
      className={`fixed inset-0 z-[100] flex items-center justify-center bg-background transition-opacity duration-600 ${
        isAnimatingOut ? "opacity-0" : "opacity-100"
      }`}
    >
      <div className="flex flex-col items-center gap-8">
        <div
          className={`transition-all duration-700 ${
            isAnimatingOut ? "scale-110 opacity-0" : "scale-100 opacity-100"
          }`}
        >
          <img
            src={minzoLogo}
            alt="MINZO"
            className="h-20 w-auto md:h-32 animate-pulse"
          />
        </div>

        <div className="w-48 h-[2px] bg-muted overflow-hidden">
          <div
            className={`h-full bg-foreground transition-all ease-out ${
              isAnimatingOut ? "w-full duration-300" : "w-0"
            }`}
            style={{
              animation: isAnimatingOut
                ? "none"
                : `loadingBar ${maxDisplayTime}ms ease-out forwards`,
            }}
          />
        </div>

        <p
          className={`text-sm tracking-[0.2em] text-muted-foreground uppercase transition-opacity duration-500 ${
            isAnimatingOut ? "opacity-0" : "opacity-100"
          }`}
        >
          Interior Excellence
        </p>
      </div>

      <style>{`
        @keyframes loadingBar {
          0% { width: 0%; }
          100% { width: 100%; }
        }
      `}</style>
    </div>
  );
}
